/**
 * ServicesManager — handles ServicesEng.json / ServicesAlb.json
 * Data shape: { services: [{ icon, service, title, description, path }] }
 */
import React, { Component } from 'react';
import AdminLayout from '../AdminLayout.jsx';
import { fetchDataFile, saveDataFile } from '../../../APICall/Api.jsx';

const FILES = { eng: 'ServicesEng.json', alb: 'ServicesAlb.json' };

const EMPTY = { icon: '', service: '', title: '', description: '', path: '' };

class ServicesManager extends Component {
  state = {
    lang: 'eng',
    rawData: null,
    items: [],
    loading: true,
    saving: false,
    error: null,
    modal: null,
    editIndex: null,
    formData: { ...EMPTY },
    savedMsg: '',
  };

  componentDidMount() { this.loadData(); }

  loadData = async () => {
    const { lang } = this.state;
    this.setState({ loading: true, error: null });
    try {
      const data = await fetchDataFile(FILES[lang]);
      this.setState({ rawData: data, items: data.services || [], loading: false });
    } catch (e) {
      this.setState({ error: 'Failed to load services.', loading: false });
    }
  };

  switchLang = (lang) => this.setState({ lang, items: [], loading: true, rawData: null }, this.loadData);

  saveData = async (newItems) => {
    this.setState({ saving: true });
    try {
      const { rawData, lang } = this.state;
      const payload = { ...rawData, services: newItems };
      await saveDataFile(FILES[lang], payload);
      this.setState({ items: newItems, saving: false, savedMsg: 'Saved!' });
      setTimeout(() => this.setState({ savedMsg: '' }), 3000);
    } catch (e) {
      this.setState({ saving: false, error: 'Save failed. Check your admin token.' });
    }
  };

  openAdd  = () => this.setState({ modal: 'add', formData: { ...EMPTY }, editIndex: null });
  openEdit = (idx) => this.setState({ modal: 'edit', editIndex: idx, formData: { ...this.state.items[idx] } });
  closeModal = () => this.setState({ modal: null, formData: { ...EMPTY } });

  handleField = (key, val) =>
    this.setState((s) => ({ formData: { ...s.formData, [key]: val } }));

  handleSave = () => {
    const { items, editIndex, formData, modal } = this.state;
    const newItems = modal === 'add'
      ? [formData, ...items]
      : items.map((it, i) => (i === editIndex ? formData : it));
    this.saveData(newItems);
    this.closeModal();
  };

  handleDelete = (idx) => {
    if (!window.confirm('Delete this service?')) return;
    const newItems = this.state.items.filter((_, i) => i !== idx);
    this.saveData(newItems);
  };

  render() {
    const { lang, items, loading, saving, modal, formData, savedMsg, error } = this.state;
    const { history, location } = this.props;

    return (
      <AdminLayout title="Manage Services" history={history} location={location}>
        {/* Toolbar */}
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 20 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
            <div className="bgg-lang-tabs">
              {['eng', 'alb'].map((l) => (
                <button
                  key={l}
                  className={`bgg-lang-tab${lang === l ? ' active' : ''}`}
                  onClick={() => this.switchLang(l)}
                >
                  {l.toUpperCase()}
                </button>
              ))}
            </div>
            {savedMsg && <span style={{ color: '#2e7d32', fontWeight: 600 }}>✓ {savedMsg}</span>}
            {error && <span style={{ color: '#e53935' }}>{error}</span>}
          </div>
          <button className="bgg-btn bgg-btn-primary" onClick={this.openAdd}>
            <i className="fas fa-plus" /> Add Service
          </button>
        </div>

        {/* Table */}
        <div className="bgg-admin-card">
          <div className="bgg-admin-card-header">
            <h2>Services ({loading ? '…' : items.length}) — {lang.toUpperCase()}</h2>
          </div>
          {loading
            ? <div style={{ padding: 32, textAlign: 'center', color: '#8a8aaa' }}>Loading…</div>
            : (
              <div style={{ overflowX: 'auto' }}>
                <table className="bgg-admin-table">
                  <thead>
                    <tr>
                      <th>#</th>
                      <th>Icon</th>
                      <th>Category</th>
                      <th>Title</th>
                      <th>Path</th>
                      <th style={{ width: 120 }}>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {items.map((item, idx) => (
                      <tr key={idx}>
                        <td style={{ color: '#aaa', width: 40 }}>{idx + 1}</td>
                        <td>
                          {item.icon
                            ? <i className={item.icon} style={{ fontSize: 20, color: '#2e7d32' }} />
                            : <span style={{ color: '#ccc' }}>–</span>}
                        </td>
                        <td><span style={{ fontSize: '0.78rem', color: '#888', textTransform: 'uppercase' }}>{item.service}</span></td>
                        <td><strong>{item.title}</strong></td>
                        <td style={{ fontSize: '0.82rem', color: '#666' }}>{item.path}</td>
                        <td>
                          <div className="bgg-table-actions">
                            <button className="bgg-btn bgg-btn-outline bgg-btn-sm" onClick={() => this.openEdit(idx)}>
                              <i className="fas fa-edit" />
                            </button>
                            <button className="bgg-btn bgg-btn-danger bgg-btn-sm" onClick={() => this.handleDelete(idx)}>
                              <i className="fas fa-trash" />
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
                {items.length === 0 && (
                  <div style={{ padding: 32, textAlign: 'center', color: '#aaa' }}>
                    No services yet. Click "Add Service" to create one.
                  </div>
                )}
              </div>
            )}
        </div>

        {/* Modal */}
        {modal && (
          <div className="bgg-modal-overlay" onClick={this.closeModal}>
            <div className="bgg-modal" onClick={(e) => e.stopPropagation()}>
              <div className="bgg-modal-header">
                <h3>{modal === 'add' ? 'Add Service' : 'Edit Service'}</h3>
                <button className="bgg-modal-close" onClick={this.closeModal}>×</button>
              </div>
              <div className="bgg-modal-body">
                {[
                  { key: 'icon',        label: 'Icon Class (FontAwesome)',  hint: 'e.g. fas fa-leaf' },
                  { key: 'service',     label: 'Category Label',            hint: 'e.g. Environmental' },
                  { key: 'title',       label: 'Title' },
                  { key: 'path',        label: 'Link Path',                 hint: 'e.g. /eng/projects' },
                ].map(({ key, label, hint }) => (
                  <div className="bgg-form-group" key={key}>
                    <label className="bgg-form-label">{label}</label>
                    <input
                      className="bgg-form-input"
                      type="text"
                      value={formData[key] || ''}
                      placeholder={hint || ''}
                      onChange={(e) => this.handleField(key, e.target.value)}
                    />
                  </div>
                ))}
                <div className="bgg-form-group">
                  <label className="bgg-form-label">Description</label>
                  <textarea
                    className="bgg-form-textarea"
                    value={formData.description || ''}
                    onChange={(e) => this.handleField('description', e.target.value)}
                    rows={4}
                  />
                </div>
                {formData.icon && (
                  <div style={{ padding: '8px 0', color: '#555', fontSize: '0.88rem' }}>
                    Preview: <i className={formData.icon} style={{ marginLeft: 6, color: '#2e7d32', fontSize: 18 }} />
                  </div>
                )}
              </div>
              <div className="bgg-modal-footer">
                <button className="bgg-btn bgg-btn-outline" onClick={this.closeModal}>Cancel</button>
                <button className="bgg-btn bgg-btn-primary" onClick={this.handleSave} disabled={saving}>
                  {saving ? 'Saving…' : 'Save'}
                </button>
              </div>
            </div>
          </div>
        )}
      </AdminLayout>
    );
  }
}

export default ServicesManager;



// WEBPACK FOOTER //
// ./src/views/Admin/managers/ServicesManager.jsx